import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Button from './ui/Button';
import Card from './ui/Card';
import { unsubscribeFromEmails, getStudentStatus } from '../services/apiService';

const UnsubscribePage = () => {
    const { institutionUrlEndpoint, studentId } = useParams();
    const [loading, setLoading] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(true);
    const [status, setStatus] = useState('initial'); // 'initial', 'already_unsubscribed', 'success', 'error'
    const [message, setMessage] = useState('');
    const [studentInfo, setStudentInfo] = useState(null);

    // Check student's current email preference status when component loads
    useEffect(() => {
        const checkStudentStatus = async () => {
            try {
                const student = await getStudentStatus(institutionUrlEndpoint, studentId);
                setStudentInfo(student);

                if (!student.wants_emails) {
                    setStatus('already_unsubscribed');
                } else {
                    setStatus('initial');
                }
            } catch (error) {
                console.error('Error checking student status:', error);
                setStatus('error');
                setMessage('Unable to load student information. Please try again later.');
            } finally {
                setCheckingStatus(false);
            }
        };

        checkStudentStatus();
    }, [institutionUrlEndpoint, studentId]);

    const handleUnsubscribe = async () => {
        setLoading(true);
        try {
            const data = await unsubscribeFromEmails(institutionUrlEndpoint, studentId);
            setStatus('success');
            setMessage(data.message);
            setStudentInfo(data.student);
        } catch (error) {
            console.error('Error unsubscribing:', error);
            setStatus('error');
            setMessage(error.message || 'Failed to unsubscribe from email notifications.');
        } finally {
            setLoading(false);
        }
    };

    if (checkingStatus) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full space-y-8">
                    <Card>
                        <div className="text-center">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">
                                Email Notifications
                            </h2>
                            <p className="text-gray-600">Checking your current notification preferences...</p>
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <Card>
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">
                            Email Notifications
                        </h2>

                        {status === 'initial' && (
                            <>
                                <p className="text-gray-600 mb-6">
                                    You are about to unsubscribe from schedule change email notifications.
                                </p>
                                <p className="text-sm text-gray-500 mb-6">
                                    You will no longer receive emails when your schedule changes.
                                </p>
                                <div className="space-y-4">
                                    <Button
                                        onClick={handleUnsubscribe}
                                        disabled={loading}
                                        variant="danger"
                                        className="w-full"
                                    >
                                        {loading ? 'Unsubscribing...' : 'Unsubscribe from Email Notifications'}
                                    </Button>
                                    <p className="text-xs text-gray-400">
                                        Note: You can re-subscribe by contacting your institution's administrator.
                                    </p>
                                </div>
                            </>
                        )}

                        {status === 'already_unsubscribed' && (
                            <>
                                <div className="mb-4">
                                    <svg className="mx-auto h-12 w-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-medium text-green-800 mb-2">
                                    Already Unsubscribed
                                </h3>
                                <div className="mt-6">
                                    <p className="text-xs text-gray-500">
                                        To re-subscribe to email notifications, please contact your institution's administrator.
                                    </p>
                                </div>
                            </>
                        )}

                        {status === 'success' && (
                            <>
                                <div className="mb-4">
                                    <svg className="mx-auto h-12 w-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-medium text-green-800 mb-2">
                                    Successfully Unsubscribed
                                </h3>
                                <p className="text-green-600 mb-4">
                                    {message}
                                </p>
                                {studentInfo && (
                                    <p className="text-sm text-gray-600">
                                        {studentInfo.first_name} {studentInfo.family_name} will no longer receive email notifications.
                                    </p>
                                )}
                            </>
                        )}

                        {status === 'error' && (
                            <>
                                <div className="mb-4">
                                    <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-medium text-red-800 mb-2">
                                    {status === 'error' && checkingStatus ? 'Unable to Load' : 'Unsubscribe Failed'}
                                </h3>
                                <p className="text-red-600 mb-4">
                                    {message}
                                </p>
                                <Button
                                    onClick={() => {
                                        setStatus('initial');
                                        setMessage('');
                                    }}
                                    variant="secondary"
                                    className="w-full"
                                >
                                    Try Again
                                </Button>
                            </>
                        )}
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default UnsubscribePage; 